import React, { useState, useEffect, useRef } from 'react';
import { 
  Database, 
  RefreshCw, 
  Download, 
  Eye, 
  ChevronDown,
  ChevronRight,
  HardDrive,
  MoreVertical
} from 'lucide-react';
import api from '../../../services/api';
import TableDetailsModal from './components/TableDetailsModal';
import UniversalSearchInput from '../../../components/common/UniversalSearchInput';

const DatabaseTables = () => {
  const [allDatabases, setAllDatabases] = useState({});
  const [filteredDatabases, setFilteredDatabases] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDatabase, setSelectedDatabase] = useState('all');
  const [selectedTable, setSelectedTable] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [sortBy, setSortBy] = useState('tableName');
  const [sortOrder, setSortOrder] = useState('asc');
  const [notification, setNotification] = useState(null);
  const [collapsedDBs, setCollapsedDBs] = useState(new Set());
  const [activeDropdown, setActiveDropdown] = useState(null);
  const databaseRefs = useRef({});

  useEffect(() => {
    fetchDatabases();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterDatabases();
  }, [allDatabases, searchTerm, selectedDatabase, sortBy, sortOrder]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };

    if (activeDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [activeDropdown]);

  const fetchDatabases = async () => {
    try {
      setLoading(true);
      const response = await api.get('/database/tables');
      const dbData = response.data.databases || {};
      setAllDatabases(dbData);
    } catch (error) {
      showNotification('Failed to fetch databases', 'error');
      console.error('Error fetching databases:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDatabases = () => {
    let filtered = {};
    const dbNames = Object.keys(allDatabases);

    // Filter by database
    const dbsToInclude = selectedDatabase === 'all' 
      ? dbNames 
      : dbNames.filter(name => name === selectedDatabase);

    // Process each database
    dbsToInclude.forEach(dbName => {
      let dbTables = allDatabases[dbName] || [];

      // Filter by search term
      if (searchTerm) {
        dbTables = dbTables.filter(table =>
          table.tableName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          dbName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          table.engine?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      // Sort tables
      dbTables.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue.toLowerCase();
        }

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      // Only include databases that have tables after filtering
      if (dbTables.length > 0) {
        filtered[dbName] = dbTables;
      }
    });

    setFilteredDatabases(filtered);
  };

  const toggleDatabaseCollapse = (dbName) => {
    const newCollapsed = new Set(collapsedDBs);
    if (newCollapsed.has(dbName)) {
      newCollapsed.delete(dbName);
    } else {
      newCollapsed.add(dbName);
    }
    setCollapsedDBs(newCollapsed);
  };

  const jumpToDatabase = (dbName) => {
    // Expand selected database
    const newCollapsed = new Set(collapsedDBs);
    newCollapsed.delete(dbName);
    setCollapsedDBs(newCollapsed);
    
    // Collapse others
    Object.keys(allDatabases).forEach(db => {
      if (db !== dbName) {
        newCollapsed.add(db);
      }
    });
    setCollapsedDBs(newCollapsed);
    
    // Scroll to database section
    setTimeout(() => {
      databaseRefs.current[dbName]?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }, 100);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleRefresh = async () => {
    await fetchDatabases();
    showNotification('Databases refreshed successfully', 'success');
  };

  const handleExport = () => {
    const allFilteredTables = Object.entries(filteredDatabases).flatMap(([dbName, tables]) =>
      tables.map(table => ({ ...table, database: dbName }))
    );
    
    const csv = [
      ['Table Name', 'Database', 'Engine', 'Row Count', 'Data Size (MB)', 'Index Size (MB)', 'Total Size (MB)', 'Created', 'Updated'],
      ...allFilteredTables.map(table => [
        table.tableName,
        table.database,
        table.engine || '',
        table.rowCount || 0,
        table.dataSize || 0,
        table.indexSize || 0,
        table.totalSize || 0,
        table.created || '',
        table.updated || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `database_tables_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDropdownToggle = (tableId, e) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === tableId ? null : tableId);
  };

  const handleAction = (action, table, e) => {
    e.stopPropagation();
    setActiveDropdown(null);
    
    switch (action) {
      case 'view':
        setSelectedTable(table);
        setShowDetailsModal(true);
        break;
      case 'export':
        // Export single table data
        const csv = [
          ['Table Name', 'Database', 'Engine', 'Row Count', 'Data Size (MB)', 'Index Size (MB)', 'Total Size (MB)', 'Created', 'Updated'],
          [
            table.tableName,
            table.database,
            table.engine || '',
            table.rowCount || 0,
            table.dataSize || 0,
            table.indexSize || 0,
            table.totalSize || 0,
            table.created || '',
            table.updated || ''
          ]
        ].map(row => row.join(',')).join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${table.database}_${table.tableName}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        showNotification(`Exported ${table.tableName} successfully`, 'success');
        break;
      case 'refresh':
        // Refresh specific table
        showNotification(`Refreshing ${table.tableName}...`, 'info');
        // Add API call to refresh table cache if needed
        break;
      default:
        break;
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const formatSize = (size) => {
    if (!size) return '0 MB';
    if (size < 1024) return `${size.toFixed(2)} MB`;
    return `${(size / 1024).toFixed(2)} GB`;
  };

  // Group tables by database is now handled by filteredDatabases state
  const dbNames = Object.keys(allDatabases);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Database className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-900">Database Tables</h1>
        </div>
        <p className="text-gray-600 mt-2">
          View and analyze all database tables across multiple databases
        </p>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-6 p-4 rounded-lg ${
          notification.type === 'error' 
            ? 'bg-red-50 border border-red-200 text-red-800' 
            : 'bg-green-50 border border-green-200 text-green-800'
        }`}>
          {notification.message}
        </div>
      )}

      {/* Filters and Actions */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <UniversalSearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search tables..."
            className="flex-1"
          />
          
          <select
            value={selectedDatabase}
            onChange={(e) => setSelectedDatabase(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Databases</option>
            {dbNames.map(dbName => (
              <option key={dbName} value={dbName}>{dbName}</option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button
              onClick={handleExport}
              disabled={Object.keys(filteredDatabases).length === 0}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Database Sections */}
      <div className="space-y-4">
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <RefreshCw className="w-8 h-8 text-gray-400 mx-auto mb-3 animate-spin" />
            <p className="text-gray-500">Loading databases...</p>
          </div>
        ) : Object.keys(filteredDatabases).length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">
              {searchTerm || selectedDatabase !== 'all' 
                ? 'No tables found matching your criteria' 
                : 'No tables found'}
            </p>
          </div>
        ) : (
          Object.entries(filteredDatabases).map(([dbName, dbTables]) => {
            const isCollapsed = collapsedDBs.has(dbName);
            
            return (
              <div 
                key={dbName} 
                ref={el => databaseRefs.current[dbName] = el}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                {/* Database Header */}
                <div 
                  className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => toggleDatabaseCollapse(dbName)}
                >
                  <div className="flex items-center gap-3">
                    {isCollapsed ? (
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-600" />
                    )}
                    <Database className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-900">{dbName}</span>
                    <span className="text-sm text-gray-500">({dbTables.length} tables)</span>
                  </div>
                </div>

                {/* Tables */}
                {!isCollapsed && (
                  <div className="divide-y divide-gray-200">
                    {/* Table Header */}
                    <div className="grid grid-cols-7 px-6 py-3 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <div 
                        className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort('tableName');
                        }}
                      >
                        Table Name
                        {sortBy === 'tableName' && (
                          <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                      <div 
                        className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort('engine');
                        }}
                      >
                        Engine
                        {sortBy === 'engine' && (
                          <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                      <div 
                        className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-right"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort('rowCount');
                        }}
                      >
                        Rows
                        {sortBy === 'rowCount' && (
                          <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                      <div 
                        className="cursor-pointer hover:bg-gray-100 px-2 py-1 rounded text-right"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort('totalSize');
                        }}
                      >
                        Size
                        {sortBy === 'totalSize' && (
                          <span className="ml-1">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </div>
                      <div className="text-center">Created</div>
                      <div className="text-center">Updated</div>
                      <div className="text-center">Actions</div>
                    </div>

                    {/* Table Rows */}
                    {dbTables.map((table) => (
                      <div 
                        key={`${table.database}-${table.tableName}`} 
                        className="grid grid-cols-7 px-6 py-3 hover:bg-gray-50 items-center text-sm"
                      >
                        <div className="font-medium text-gray-900">{table.tableName}</div>
                        <div className="text-gray-600">{table.engine || '-'}</div>
                        <div className="text-gray-900 text-right">{table.rowCount?.toLocaleString() || 0}</div>
                        <div className="flex items-center justify-end gap-2 text-gray-900">
                          <HardDrive className="w-4 h-4 text-gray-400" />
                          <span>{formatSize(table.totalSize)}</span>
                        </div>
                        <div className="text-center text-gray-600">
                          {table.created ? new Date(table.created).toLocaleDateString() : '-'}
                        </div>
                        <div className="text-center text-gray-600">
                          {table.updated ? new Date(table.updated).toLocaleDateString() : '-'}
                        </div>
                        <div className="flex justify-center relative">
                          <button
                            onClick={(e) => handleDropdownToggle(`${table.database}-${table.tableName}`, e)}
                            className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          
                          {activeDropdown === `${table.database}-${table.tableName}` && (
                            <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-10 min-w-[150px]">
                              <button
                                onClick={(e) => handleAction('view', table, e)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </button>
                              <button
                                onClick={(e) => handleAction('export', table, e)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <Download className="w-4 h-4" />
                                Export CSV
                              </button>
                              <button
                                onClick={(e) => handleAction('refresh', table, e)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                              >
                                <RefreshCw className="w-4 h-4" />
                                Refresh
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Quick Database Jump */}
      <div className="fixed bottom-6 right-6 bg-white rounded-lg shadow-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Quick Database Jump
        </label>
        <select
          onChange={(e) => {
            if (e.target.value) {
              jumpToDatabase(e.target.value);
              e.target.value = '';
            }
          }}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        >
          <option value="">Select Database</option>
          {dbNames.map(dbName => (
            <option key={dbName} value={dbName}>{dbName}</option>
          ))}
        </select>
      </div>

      {/* Summary */}
      <div className="mt-6 bg-gray-50 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-sm text-gray-600">Total Tables</p>
            <p className="text-2xl font-bold text-gray-900">
              {Object.values(filteredDatabases).reduce((sum, tables) => sum + tables.length, 0)}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Rows</p>
            <p className="text-2xl font-bold text-gray-900">
              {Object.values(filteredDatabases).flat().reduce((sum, t) => sum + (t.rowCount || 0), 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Size</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatSize(Object.values(filteredDatabases).flat().reduce((sum, t) => sum + (t.totalSize || 0), 0))}
            </p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Last Updated</p>
            <p className="text-2xl font-bold text-gray-900">
              {new Date().toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>

      {/* Table Details Modal */}
      {showDetailsModal && selectedTable && (
        <TableDetailsModal
          table={selectedTable}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTable(null);
          }}
        />
      )}
    </div>
  );
};

export default DatabaseTables;
